import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { VersionsListItem } from 'src/features/plan-version-sidebar/versions-list-item';
import Collapse, { Panel } from 'src/shared/components/collapse/collapse';
import { USER_ADMIN_ROLE } from 'src/shared/constants/user';
import { useStore } from 'src/store';
import { TPlanVersion, TArchivedPlansGroup, PlanVersionType } from 'src/store/comparison/types';

import styles from './archived-versions-group.module.scss';

type Props = {
  versionsGroup: TArchivedPlansGroup;
  onDeletePlanVersion(planVersion: TPlanVersion): void;
  onVersionChange(planVersion: TPlanVersion): void;
};

export const ArchivedVersionsGroup = observer(function ArchivedVersionsGroup({
  versionsGroup,
  onDeletePlanVersion,
  onVersionChange,
}: Props) {
  const { t } = useTranslation();
  const { notifications, auth, planVersion } = useStore();

  const isAdmin = !!auth.userInfo?.realm_access.roles?.includes(USER_ADMIN_ROLE);
  const currentVersionId = planVersion.version?.id;

  if (!versionsGroup.months.length) {
    return null;
  }

  const handleDeleteVersion = (version: TPlanVersion) => {
    if (version.data.availableForDashboardLite) {
      notifications.showErrorMessage(t('drawers:planVersion.deleteAnalyticsVersionError'));
    } else {
      onDeletePlanVersion(version);
    }
  };

  return (
    <Collapse key={versionsGroup.year} defaultActiveKey={versionsGroup.year}>
      <Panel key={versionsGroup.year} header={versionsGroup.year} className={styles.text}>
        {versionsGroup.months.map((monthGroup) => (
          <Collapse key={monthGroup.month} defaultActiveKey={monthGroup.month}>
            <Panel key={monthGroup.month} header={t(monthGroup.month)} className={styles.text}>
              <ul className={styles.versionsList}>
                {monthGroup.plans.map((plan) => {
                  const isDeletingAvailable = isAdmin && currentVersionId !== plan.id;
                  const isRenamingAvailable = plan.data.versionType !== PlanVersionType.draft && isAdmin;

                  return (
                    <VersionsListItem
                      key={plan.id}
                      version={plan}
                      isRenamingAvailable={isRenamingAvailable}
                      onRename={planVersion.renameVersion}
                      onDeleteVersion={isDeletingAvailable ? handleDeleteVersion : undefined}
                      onVersionChange={onVersionChange}
                    />
                  );
                })}
              </ul>
            </Panel>
          </Collapse>
        ))}
      </Panel>
    </Collapse>
  );
});
